import parlors from "@/constants/parlors_detail.json";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";

import { getThreeClosestParlorFrom } from "@/containers/Parlor/ParlorDetailContainer/utils/getThreeClosestParlorFrom";
import { BinsoObject, ParlorDetailProps } from "@/types/parlor";
import numberToLocalString from "@/utils/numberToLocalString";
import { Box } from "@chakra-ui/react";
import dayjs from "dayjs";
import { FUNERAL_SIGUNGU } from "goi_common";
import NotFoundPage from "pages/404";

import { ParlorEstimatesContainer } from "@/containers/Parlor/ParlorPriceTypeContainer";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import { useRouter } from "next/router";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors.map((parlor) => ({
    params: {
      id: parlor.id.toString(),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";
  let closestParlorsByService = {};
  let discountDescription =
    parlors_discount.find((p) => p.parlor_id == Number(id))?.description || null;
  if (id) {
    parlor = parlors.find((parlor) => parlor.id === parseInt(params?.id as string));
    if (parlor) {
      if (!discountDescription) {
        discountDescription = ["21", "26", "38"].includes(parlor.sido)
          ? "20%이상 할인받을 수 있어요"
          : null;
      }

      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      let binsoString = "";
      parlor.binsos.slice(0, 2).forEach((binso: BinsoObject) => {
        binsoString += binso.pyeong
          ? `${binso.pyeong.toFixed()}평형 ${numberToLocalString(binso.charge)}원, `
          : "";
      });

      description =
        name +
        " 정보, 비용 안내, 견적서, 예상 견적, 예상 비용 견적, 근조화환발송, " +
        name +
        " 빈소 비용 (1일/24시간 기준)\n" +
        binsoString.slice(0, binsoString.length - 2) +
        "\n" +
        name +
        " 사전 예약시 빈소 할인. 예상 비용: " +
        parlor.expected_charge_sum["무빈소"].toLocaleString() +
        "원~" +
        parlor.expected_charge_sum["일반장"].toLocaleString() +
        "원. " +
        "주소, 전화번호, 빈소 비용안내";

      closestParlorsByService = {
        무빈소: getThreeClosestParlorFrom(parlor as any, "무빈소"),
        가족장: getThreeClosestParlorFrom(parlor as any, "가족장"),
        일반장: getThreeClosestParlorFrom(parlor as any, "일반장"),
      };
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${name} 위치, ${name} 정보, 비용안내, ${district} 장례식장, ${name} 근조화환발송`,
      pageOgTitle:
        ((parlor && Number(parlor.id)) === 272 ? "☎☎ 1666-9784(장례상담) ☎☎" : "") +
        `${name}(${FUNERAL_SIGUNGU.get(Number(parlor?.sido))?.get(
          Number(parlor?.sigungu),
        )}) 예상 비용 견적 - [장례의 모든 것, 고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      hasError: !parlor,
      closestParlorsByService,
      discountDescription,
    },
  };
};

export const excludeParlors = [
  "76",
  "194",
  "247",
  "262",
  "325",
  "326",
  "595",
  "611",
  "641",
  "792",
  "996",
];

function ParlorId({
  defaultParlor,
  hasError,
  closestParlorsByService,
  discountDescription,
  pageDescription,
}: {
  defaultParlor: ParlorDetailProps;
  hasError: boolean;
  closestParlorsByService: any;
  discountDescription?: string;
  pageDescription: string;
}) {
  const router = useRouter();
  if (hasError) return <NotFoundPage />;
  const today = dayjs().format("YYYY-MM-DD");
  // const { howtoRichSearch, carouselRichSearch, reviewResult76 } = richSearchData.parlor;
  // const { parlorFaqList, reviewResult } = richSearchData.parlor;

  return (
    <>
      <Box position="absolute" marginTop="-90px">
        <time dateTime={today} className="white se_publishDate pcol2 date">
          {today}
        </time>
      </Box>
      <Box position="absolute" marginTop="-180px" color="#ffffffff">
        <h1>{defaultParlor.companyname} 예상 비용 견적서</h1>
        <p>{pageDescription}</p>
      </Box>

      {router.query && router.query.id && (
        <ParlorEstimatesContainer
          parlorId={router.query.id as string}
          expected_charge_sum={defaultParlor["expected_charge_sum"]}
        />
      )}
    </>
  );
}

// function ParlorId({
//   defaultParlor,
//   hasError,
// }: {
//   defaultParlor: ParlorDetailProps;
//   hasError: boolean;
// }) {
//   const { data: parlor, refetch } = useQuery(
//     ["parlor", defaultParlor?.id],
//     () => getParlorDetail(defaultParlor?.id),
//     {
//       refetchOnMount: false,
//       refetchOnWindowFocus: false,
//       refetchOnReconnect: false,
//     },
//   );

//   const parlorNameFromConstant = parlors.find(
//     (parlor) => parlor.id === parseInt(defaultParlor?.id as string),
//   )?.companyname;

//   if (hasError) return <NotFoundPage />;

//   return (
//     <>
//       <Head>
//         <title>{parlorNameFromConstant} 비용, 정보, 후기 - [장례를 바로잡다, 고이]</title>
//       </Head>
//       {parlor ? (
//         <ParlorDetailContainer parlor={parlor} refetch={refetch} />
//       ) : (
//         <ParlorDetailSkeleton />
//       )}
//     </>
//   );
// }

export default ParlorId;
